import { Routes, Route, Navigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'

import Layout from '@/components/Layout'
import AutoLogoutWarning from '@/components/AutoLogoutWarning'
import ProductionPlanForm from '@/components/ProductionPlanForm';
import ProductionPlanView from '@/components/ProductionPlanView';
import { autoLogoutManager } from '@/utils/autoLogout'
import Dashboard from '@/pages/Dashboard'
import Users from '@/pages/Users'
import Productions from '@/pages/Productions'
import ProductionPlans from '@/pages/ProductionPlans'
import ProductionGuides from '@/pages/ProductionGuides'
import AdminProductionMessages from '@/pages/AdminProductionMessages'
import Messages from '@/pages/Messages'
import Communication from '@/pages/Communication'
import FarmerProfile from '@/pages/FarmerProfile'
import Marketplace from '@/pages/Marketplace'
import Lots from '@/pages/Lots'
import Auctions from '@/pages/Auctions'
import AllBids from '@/pages/AllBids'
import AllOrders from '@/pages/AllOrders'
import AllBuyerRequirements from '@/pages/AllBuyerRequirements'
import Transactions from '@/pages/Transactions'
import Reports from '@/pages/Reports'
import MarketAnalytics from '@/pages/MarketAnalytics'
import Analytics from '@/pages/Analytics'
import Settings from '@/pages/Settings'
import Articles from '@/pages/Articles'
import ArticleForm from '@/pages/ArticleForm'
import Tags from '@/pages/Tags'
import Categories from '@/pages/Categories'
import Centres from '@/pages/Centres'
import Login from '@/pages/Login'
import AutoLogoutTest from '@/components/AutoLogoutTest'
import { useAppSelector, useAppDispatch } from '@/hooks/redux'

function App() {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  const [showWarning, setShowWarning] = useState(false)
  const [warningTimeLeft, setWarningTimeLeft] = useState(0)

  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('token')
      if (token) {
        const handleLogout = () => {
          setShowWarning(false)
          // Dispatch logout action to update Redux state and clear localStorage
          dispatch({ type: 'auth/logout' })
        }

        const handleWarning = (timeLeft: number) => {
          setWarningTimeLeft(timeLeft)
          setShowWarning(true)
        }

        autoLogoutManager.initialize(handleLogout, handleWarning)

        return () => {
          autoLogoutManager.cleanup()
        }
      }
    }
  }, [isAuthenticated, dispatch])

  const handleExtendSession = () => {
    setShowWarning(false)
    autoLogoutManager.extendSession()
  }

  const handleLogoutNow = () => {
    setShowWarning(false)
    dispatch({ type: 'auth/logout' })
  }

  if (!isAuthenticated) {
    return <Login />
  }

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/users" element={<Users />} />
          <Route path="/buyer-requirements" element={<AllBuyerRequirements />} />
          <Route path="/productions" element={<Productions />} />
          <Route path="/productions/:productionId/messages" element={<AdminProductionMessages />} />
          <Route path="/farmers/:farmerId" element={<FarmerProfile />} />
          <Route path="/farmers/:farmerId/messages" element={<AdminProductionMessages />} />
          <Route path="/production-plans" element={<ProductionPlans />} />
          <Route path="/production-plans/new" element={<ProductionPlanForm />} />
          <Route path="/production-plans/edit/:id" element={<ProductionPlanForm />} />
          <Route path="/production-plans/:id" element={<ProductionPlanView />} />
          <Route path="/production-guides" element={<ProductionGuides />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/lots" element={<Lots />} />
          <Route path="/auctions" element={<Auctions />} />
          <Route path="/all-bids" element={<AllBids />} />
          <Route path="/all-orders" element={<AllOrders />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/market-analytics" element={<MarketAnalytics />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/communication" element={<Communication />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/centres" element={<Centres />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/create" element={<ArticleForm />} />
          <Route path="/articles/edit/:id" element={<ArticleForm />} />
          <Route path="/tags" element={<Tags />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/auto-logout-test" element={<AutoLogoutTest />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Box>
      
      <AutoLogoutWarning
        isVisible={showWarning}
        timeLeft={warningTimeLeft}
        onExtendSession={handleExtendSession}
        onLogout={handleLogoutNow}
      />
    </Layout>
  )
}

export default App
